// src/services/NotificationService.ts

import { onMessage, getMessaging, MessagePayload } from "firebase/messaging";
import { messaging } from "../config/firebase";
import { toast } from "react-hot-toast";
import TokenManager from "./TokenManager";

export class NotificationService {
  private static instance: NotificationService;
  private tokenManager: TokenManager;
  private isInitialized: boolean = false;

  private messageUnsubscribe: (() => void) | null = null;

  private constructor() {
    this.tokenManager = TokenManager.getInstance();
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  public async initialize(): Promise<void> {
    console.log("A. Starting service initialization");
    if (this.isInitialized) {
      console.log("B. Already initialized");
      return;
    }

    try {
      console.log("C. Registering service worker");
      await this.registerServiceWorker();
      console.log("D. Service worker registered");

      const permission = await this.requestPermission();
      console.log("E. Permission received:", permission);

      if (permission === "granted") {
        await this.setupMessageListener();
        console.log("F. Message listener setup complete");
      }

      this.isInitialized = true;
    } catch (error) {
      console.error("Service initialization failed:", error);
      throw error;
    }
  }

  private async registerServiceWorker(): Promise<void> {
    if ("serviceWorker" in navigator) {
      try {
        console.log("SW1. Starting service worker registration");

        // Check if there's already an active service worker
        const existingReg = await navigator.serviceWorker.getRegistration();
        console.log("SW2. Existing registration:", existingReg);

        const registration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
          {
            scope: "/",
          }
        );

        console.log("SW3. Registration state:", {
          installing: !!registration.installing,
          waiting: !!registration.waiting,
          active: !!registration.active,
        });

        // Wait for service worker to become active
        if (!registration.active) {
          console.log("SW4. Waiting for activation...");
          await new Promise<void>((resolve) => {
            registration.addEventListener("activate", () => {
              console.log("SW5. Service Worker activated");
              resolve();
            });
          });
        } else {
          console.log("SW5. Service Worker already active");
        }
      } catch (error) {
        console.error("SW-ERROR: Service worker registration failed:", error);
        throw error;
      }
    }
  }

  private async waitForActiveServiceWorker(): Promise<void> {
    const maxAttempts = 10;
    let attempts = 0;

    while (attempts < maxAttempts) {
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration?.active) {
        console.log("Service worker is active");
        return;
      }

      console.log("Waiting for service worker to activate...");
      await new Promise((resolve) => setTimeout(resolve, 500));
      attempts++;
    }

    throw new Error("Service worker failed to activate in time");
  }

  public async requestPermission(): Promise<NotificationPermission> {
    try {
      // Remove service worker registration from here
      // since it's already done in initialize()
      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        await this.tokenManager.getValidToken();
      }
      return permission;
    } catch (error) {
      console.error("Failed to request permission:", error);
      throw error;
    }
  }

  public getCurrentPermission(): NotificationPermission {
    return Notification.permission;
  }

  private async setupMessageListener(): Promise<void> {
    // Early return if messaging is null
    if (!messaging) {
      console.warn("Messaging not initialized");
      return;
    }

    // Cleanup old listener
    if (this.messageUnsubscribe) {
      this.messageUnsubscribe();
    }

    // Setup new listener with proper type check
    this.messageUnsubscribe = onMessage(
      messaging,
      (payload: MessagePayload) => {
        if (document.visibilityState === "visible") {
          this.showNotification(payload);
        }
      }
    );
  }

  // NotificationService.ts
  // In NotificationService.ts
  // In NotificationService.ts
  private showNotification(payload: MessagePayload): void {
    if (!payload.notification) return;

    const { title, body } = payload.notification;

    toast(body || "", {
      duration: 4000,
      id: Date.now().toString(), // Unique ID for each toast
    });
  }
}

export default NotificationService;
